/**
 * Which progression surfaces are open, and nothing else.
 *
 * ── Why this is a store rather than component state ─────────────────────────────────────────
 * The 02.08 gallery scatters one exercise's progression across three places inside its card — the
 * rule chip in the header, the "System progresji" group in the side column, the cycle matrix under
 * the table — and puts the rule EDITOR somewhere else entirely: `Builder progresji v2` is a
 * full-width sheet over the whole day modal, not a panel inside a card.
 *
 * Three siblings and one distant cousin cannot share `useState`, and hoisting the state into the day
 * form would put a progression concern into a module that has no business knowing what a rule is.
 * So the feature owns its own disclosure, keyed by slot token, and every one of its four surfaces
 * reads the answer it needs.
 *
 * ── One sheet at a time, many matrices ──────────────────────────────────────────────────────
 * The sheet covers the whole day, so a second one would have nothing to open into: `openRuleSlot`
 * is a single token. The matrices are independent, because a trainer comparing
 * two exercises' projections is reading, not editing, and closing one to open another would make
 * that impossible.
 *
 * ── Focus return is OURS ────────────────────────────────────────────────────────────────────
 * The drawer is not a Base UI popup, so nothing restores focus when it unmounts and a keyboard
 * trainer who pressed ✕ or Escape was left standing on `<body>` — the next Escape then closed the
 * whole day. The same mechanism the day dock already uses answers it (`useDayDock`'s
 * `returnFocusToken`): the store names the slot that was just closed, and that exercise's chip —
 * which is still mounted, unlike a dialog's opener in fullscreen — claims focus and clears it.
 */

import { create } from 'zustand';

interface ProgressionDisclosure {
  /** The exercise whose rule sheet is out, or `null` when none is. */
  readonly openRuleSlot: string | null;
  /** Every exercise whose cycle matrix is expanded. */
  readonly expandedMatrices: ReadonlySet<string>;
  /** The exercise whose chip is owed focus back. `null` most of the time. */
  readonly returnFocusToken: string | null;
  readonly openRule: (exerciseSlotToken: string) => void;
  readonly closeRule: () => void;
  /** The chip took the focus it was owed. Called once, by the chip itself. */
  readonly clearReturnFocus: () => void;
  readonly toggleMatrix: (exerciseSlotToken: string) => void;
}

export const useProgressionDisclosure = create<ProgressionDisclosure>((set) => ({
  openRuleSlot: null,
  expandedMatrices: new Set<string>(),
  returnFocusToken: null,
  openRule: (exerciseSlotToken) => {
    set({ openRuleSlot: exerciseSlotToken, returnFocusToken: null });
  },
  closeRule: () => {
    set((state) => ({ openRuleSlot: null, returnFocusToken: state.openRuleSlot }));
  },
  clearReturnFocus: () => {
    set({ returnFocusToken: null });
  },
  toggleMatrix: (exerciseSlotToken) => {
    set((state) => {
      const next = new Set(state.expandedMatrices);
      if (next.has(exerciseSlotToken)) next.delete(exerciseSlotToken);
      else next.add(exerciseSlotToken);
      return { expandedMatrices: next };
    });
  },
}));

/** Is this exercise's matrix expanded? A boolean, so a card re-renders only when its own answer moves. */
export function useIsMatrixExpanded(exerciseSlotToken: string): boolean {
  return useProgressionDisclosure((state) => state.expandedMatrices.has(exerciseSlotToken));
}

/** Is this exercise's chip owed the focus a just-closed drawer gave up? A boolean, for the same reason. */
export function useOwedFocus(exerciseSlotToken: string): boolean {
  return useProgressionDisclosure((state) => state.returnFocusToken === exerciseSlotToken);
}
